import type { DevControl } from "@/modules/dev/types/DevPreview";

import { z } from "zod";

export const DEV_VIEWPORTS = ["fit", "phone", "tablet"] as const;

export type DevViewport = (typeof DEV_VIEWPORTS)[number];

export const DEV_VIEWPORT_LABELS: Readonly<Record<DevViewport, string>> = {
  fit: "Fit",
  phone: "Phone",
  tablet: "Tablet",
};

/**
 * A boolean search param that also accepts `1` and `0`, so hand-typed links
 * such as `?presenter=1` work. Invalid values read as absent.
 */
export const devSearchBoolean = z.coerce.boolean().optional().catch(undefined);

/** Search params the dev shell owns on every `/dev/*` route. */
export const devShellSearchSchema = z.object({
  viewport: z.enum(DEV_VIEWPORTS).optional().catch(undefined),
  presenter: devSearchBoolean,
  controls: devSearchBoolean,
});

export type DevShellSearch = z.infer<typeof devShellSearchSchema>;

type OptionValue<Control extends DevControl> = NonNullable<
  Control["options"]
>[number]["value"];

type StateControlKey<Control extends DevControl> =
  Control["kind"] extends "action" ? never : Control["key"];

/** The validated search of a preview's state controls, keyed by the registry. */
type ControlSearch<Controls extends readonly DevControl[]> = {
  [
    Control in Controls[number] as StateControlKey<Control>
  ]?: Control["kind"] extends "boolean" ? boolean : OptionValue<Control>;
};

type ControlSearchInput<Controls extends readonly DevControl[]> = {
  [Control in Controls[number] as StateControlKey<Control>]?: unknown;
};

/**
 * The search schema of a preview's state controls: booleans as
 * `devSearchBoolean`, enums and selects as their option values. Invalid
 * values read as absent, so the preview shows the control's default and
 * links keep leaving defaults out.
 */
export function controlsToSearchSchema<Controls extends readonly DevControl[]>(
  controls: Controls,
) {
  const shape: Record<string, z.ZodType> = {};

  for (const control of controls) {
    if (control.kind === "boolean") {
      shape[control.key] = devSearchBoolean;
    } else if (control.kind === "enum" || control.kind === "select") {
      const values = (control.options ?? []).map((option) => option.value);
      shape[control.key] = z
        .enum(values as [string, ...string[]])
        .optional()
        .catch(undefined);
    }
  }

  // The loop can't carry the per-key types the registry entry spells out.
  return z.object(shape) as unknown as z.ZodType<
    ControlSearch<Controls>,
    ControlSearchInput<Controls>
  >;
}
